import { useEffect, useMemo, useRef, useState } from 'react';
import { AskButton } from '@/components/AskButton';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { listOfComment, listOfReview, reviewWaiting, useComments, waiting, type CommentItem, type ItemNote, type ReviewItem } from '@/lib/comments';
import { useStoredChoice } from '@/lib/storedChoice';
import { useGoBack } from '@/lib/goBack';
import { SheetOption, useOverlay } from '@/components/Overlay';
import { AiLeo } from '@/components/AiLeo';
import { YouButton } from '@/components/YouButton';
import { BackIcon, EditIcon, NoteIcon, PlusIcon, ReplyIcon, SendIcon, SparkIcon, TrashIcon } from '@/components/Icons';

// Comments (Gary, 3 Nov 2026; mock-up v2 approved: "comments that work like chats"). A short list, one row per
// comment or review, the same rows as Chats; tap one and it opens like a chat: our ad or post pinned on top, the
// comment as their message, our reply as ours, and Leo's reply already in the message box. Since 3 Nov 2026 the
// Chats parts too: the On duty switch per comment, team notes, Tell Leo, and + for Leave, Hide and the rest.
// Leo on duty sends his reply himself 10 minutes after a comment unless someone acts first.
// Head office answers; the Comments permission at view reads only.

// Gary, 3 Nov 2026 ("start again"): Needs you is what a person must answer; Leo is what he answered, closed or is
// about to send; Done is what a person answered or closed. Comments and reviews together in each.
const FILTERS = ['Needs you', 'Leo', 'Done'] as const;
const PLATFORM: Record<string, string> = { facebook: 'Facebook', instagram: 'Instagram', google: 'Google' };
const MARK: Record<string, string> = { facebook: 'f', instagram: '◎', google: 'G' };

function when(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins} min`;
  if (d.toDateString() === new Date().toDateString()) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** What the screen says while each action waits for Facebook, Instagram, Google or Leo. */
const PENDING: Record<string, (where: string) => string> = {
  reply: (w) => `Sending to ${w}…`,
  leave: () => 'Closing it…',
  hide: (w) => `Changing it on ${w}…`,
  suggest: () => 'Leo is writing a suggestion…',
  tell_leo: () => 'Telling Leo, he is writing it again…',
  note: () => 'Adding the note…',
  comment_duty: () => 'Switching who is on duty…',
  hold: () => 'Stopping Leo…',
  delete_reply: (w) => `Taking our reply off ${w}…`,
};

function stars(r: ReviewItem): string {
  if (r.stars != null) return `${'★'.repeat(r.stars)}${'☆'.repeat(5 - r.stars)}`;
  return r.recommends === false ? 'Does not recommend' : 'Recommends';
}

function placeName(c: CommentItem): string {
  return c.post_kind === 'ad' ? `Ad: ${c.ad_name || 'unnamed'}` : `${PLATFORM[c.platform]} post`;
}

/** The row's tag: where the comment stands. */
function tag(c: CommentItem): { text: string; cls: string } {
  if (c.reply_text) return { text: c.replied_by_leo ? 'Leo replied' : 'Replied', cls: '' };
  if (c.left_at) return { text: c.left_by_name === 'Leo' ? 'Leo: no reply' : 'Done', cls: '' };
  if (c.hidden) return { text: 'Hidden', cls: '' };
  if (c.leo_send_at) return { text: 'Leo sending', cls: ' chats-cmt-tag--leo' };
  if (c.leo_kind === 'no_reply') return { text: 'No reply needed', cls: '' };
  return { text: 'Needs you', cls: ' chats-cmt-tag--wait' };
}

function Thumb({ url, platform, small = false }: { url: string | null; platform: string; small?: boolean }) {
  return (
    <span className={`chats-cmt-thumb${small ? ' chats-cmt-thumb--sm' : ''}`}>
      {url && <img src={url} alt="" referrerPolicy="no-referrer" />}
      {!small && <small className={`chats-cmt-mark chats-cmt-mark--${platform}`}>{MARK[platform]}</small>}
    </span>
  );
}

export function CommentsScreen() {
  const { access } = useAuth();
  const level = access.comments ?? 'off';
  const { items, reviews, pages, leoOn, canAct, loaded, error, setDuty } = useComments(level !== 'off');
  const { toast } = useOverlay();
  const navigate = useNavigate();
  const { id: openId } = useParams();
  const [filter, setFilter] = useStoredChoice<(typeof FILTERS)[number]>('chats.commentsList', 'Needs you', FILTERS);
  const pageName = useMemo(() => new Map(pages.map((p) => [p.id, p.label])), [pages]);

  type Entry = { kind: 'c'; c: CommentItem; at: string } | { kind: 'r'; r: ReviewItem; at: string };
  const all: (Entry & { list: (typeof FILTERS)[number] })[] = [
    ...items.map((c) => ({ kind: 'c' as const, c, at: c.commented_at, list: listOfComment(c) })),
    ...reviews.map((r) => ({ kind: 'r' as const, r, at: r.reviewed_at, list: listOfReview(r) })),
  ].sort((a, b) => b.at.localeCompare(a.at));
  const list = all.filter((e) => e.list === filter);
  const count = (f: (typeof FILTERS)[number]) => all.filter((e) => e.list === f).length;

  function flipDuty() {
    void setDuty(!leoOn).then(
      () => toast(leoOn ? 'Leo off: every comment and review comes to Needs you' : 'Leo on duty: he answers routine comments after 10 minutes and good reviews after an hour; the rest come to Needs you'),
      (e: unknown) => toast(e instanceof Error ? e.message : 'That did not save. Try again.'),
    );
  }

  return (
    <>
      <header className="chats-listhead">
        <div className="chats-listhead-row">
          <YouButton />
          <h1 className="chats-listhead-title">Comments</h1>
          {level !== 'off' && (
            <button type="button" className={`chats-chip chats-duty${leoOn ? ' chats-duty--leo' : ' active'}`} disabled={!canAct} onClick={flipDuty} aria-pressed={leoOn}
              title={leoOn ? 'Leo answers routine comments and good reviews. Tap to answer everything yourself.' : 'You answer everything. Tap to put Leo on duty.'}>
              {leoOn ? 'Leo on duty' : 'You'}
            </button>
          )}
          <AskButton />
        </div>
      </header>

      {level === 'off' ? (
        <main className="chats-screen">
          <p className="chats-empty">Comments and reviews from our Facebook, Instagram and Google show here for people with the Comments permission. Ask head office if you need it.</p>
        </main>
      ) : (
        <>
          <div className="chats-chips">
            {FILTERS.map((f) => (
              <button key={f} type="button" className={`chats-chip${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
                {f}{f === 'Needs you' && count(f) > 0 && <span className="chats-badge chats-badge--sm chats-chip-count">{count(f)}</span>}
              </button>
            ))}
          </div>
          <main className="chats-screen">
            {error && <p className="chats-empty">{error}</p>}
            {!error && loaded && list.length === 0 && (
              <p className="chats-empty">{filter === 'Needs you' ? 'Nothing needs you. New comments come in as they are posted.' : 'Nothing here yet.'}</p>
            )}
            {list.map((e) => {
              if (e.kind === 'r') return <ReviewRow key={e.r.id} r={e.r} active={openId === e.r.id} place={(pageName.get(e.r.page_id) ?? '').replace(/^Google /, '')} onOpen={() => navigate(`/comments/r/${e.r.id}`)} />;
              const c = e.c;
              const t = tag(c);
              return (
                <button key={c.id} type="button" className={`chats-row${openId === c.id ? ' chats-row--active' : ''}`} onClick={() => navigate(`/comments/c/${c.id}`)}>
                  <Thumb url={c.post_picture_url} platform={c.platform} />
                  <div className="chats-row-main">
                    <div className="chats-row-top">
                      <span className="chats-row-name">{c.author_name || `${PLATFORM[c.platform]} user`}</span>
                      <span className={`chats-row-time${t.text === 'Needs you' ? ' chats-row-time--hot' : ''}`}>{when(c.commented_at)}</span>
                    </div>
                    <div className="chats-row-bottom">
                      <span className="chats-row-msg"><span className="chats-row-msg-text">{c.reply_text ? '✓ ' : ''}{c.body || 'A sticker or photo'}</span></span>
                      <span className={`chats-cmt-tag${t.cls}`}>{t.text}</span>
                    </div>
                    <div className="chats-cmt-place">{placeName(c)} · {pageName.get(c.page_id) ?? ''}</div>
                  </div>
                </button>
              );
            })}
          </main>
        </>
      )}
    </>
  );
}

function ReviewRow({ r, active, place, onOpen }: { r: ReviewItem; active: boolean; place: string; onOpen: () => void }) {
  return (
    <button type="button" className={`chats-row${active ? ' chats-row--active' : ''}`} onClick={onOpen}>
      <Thumb url={r.reviewer_photo_url ?? null} platform={r.platform} />
      <div className="chats-row-main">
        <div className="chats-row-top">
          <span className="chats-row-name">{r.reviewer_name || `${PLATFORM[r.platform]} user`} <span className={(r.stars ?? 5) <= 3 || r.recommends === false ? 'chats-cmt-low' : 'chats-cmt-stars'}>{stars(r)}</span></span>
          <span className={`chats-row-time${reviewWaiting(r) && !r.leo_send_at ? ' chats-row-time--hot' : ''}`}>{when(r.reviewed_at)}</span>
        </div>
        <div className="chats-row-bottom">
          <span className="chats-row-msg"><span className="chats-row-msg-text">{r.reply_text ? '✓ ' : ''}{r.body || 'Rating only'}</span></span>
          <span className={`chats-cmt-tag${reviewWaiting(r) ? (r.leo_send_at ? ' chats-cmt-tag--leo' : ' chats-cmt-tag--wait') : ''}`}>{r.reply_text ? (r.replied_by_leo ? 'Leo replied' : 'Replied') : r.left_at ? 'Done' : r.leo_send_at ? 'Leo sending' : 'Needs you'}</span>
        </div>
        <div className="chats-cmt-place">{PLATFORM[r.platform]} review · {place}</div>
      </div>
    </button>
  );
}

/** One line of the conversation: their comment, our reply, or a team note, in time order. */
type Line =
  | { kind: 'theirs'; at: string }
  | { kind: 'ours'; at: string }
  | { kind: 'note'; at: string; note: ItemNote };

/**
 * One comment or review, open like a chat (Gary, 3 Nov 2026: "feel and work very similar to the way chats work").
 * The page agreed with Gary (3 Nov 2026, "reset"): top line Hide, Done and On duty; our post, their comment, then one
 * Leo card with Send, Edit and Done; the box for your own words; + for Team note, Tell Leo, Leo again, Change reply.
 * Every button says what it is doing, then what happened. A public reply has no photos or voice notes.
 */
export function CommentThreadScreen({ kind }: { kind: 'c' | 'r' }) {
  const { id } = useParams();
  const { access } = useAuth();
  const { items, reviews, pages, notes, leoOn, canAct, loaded, act, reviewAct } = useComments((access.comments ?? 'off') !== 'off');
  const goBack = useGoBack();
  const { openSheet, closeSheet } = useOverlay();
  const comment = kind === 'c' ? items.find((i) => i.id === id) : undefined;
  const review = kind === 'r' ? reviews.find((r) => r.id === id) : undefined;
  const pageName = (pid: string) => (pages.find((p) => p.id === pid)?.label ?? '').replace(/^Google /, '');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  // The box's mode, as in Chats: a public reply, a team note, or Tell Leo.
  const [mode, setMode] = useState<'reply' | 'note' | 'leo'>('reply');
  // The reply just sent, shown at once while the list catches up.
  const [sent, setSent] = useState<string | null>(null);
  // What happened, said in the conversation as Chats does, not in a pop-up (Gary, 3 Nov 2026).
  const [notice, setNotice] = useState<string | null>(null);
  // What is being done right now, said in the conversation and on the button pressed (Gary, 3 Nov 2026: "no
  // feedback when you press something while we wait").
  const [pending, setPending] = useState<string | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const suggestion = comment?.leo_suggestion ?? review?.leo_suggestion ?? null;
  const replied = comment?.reply_text ?? review?.reply_text ?? sent;
  // The box starts empty (Gary, 3 Nov 2026: "why have Leo's suggestion in a live chat box"): Leo's reply shows as
  // his suggestion in the conversation, with Send, Edit and Leo again. Edit or Change reply put words in the box.
  useEffect(() => { setChanging(false); setMode('reply'); setText(''); }, [id]);
  useEffect(() => { setSent(null); setNotice(null); }, [id]);
  // Leo drafts by himself when a waiting one opens without a draft (no "Leo again" needed first).
  const asked = useRef<string | null>(null);
  const needsDraft = canAct && !replied && !suggestion && !!(comment ? comment.body && waiting(comment) && comment.leo_kind !== 'no_reply' : review);
  useEffect(() => {
    if (!needsDraft || !id || asked.current === id) return;
    asked.current = id;
    void (comment ? act('suggest', id) : reviewAct('suggest', id)).catch(() => undefined);
  }, [needsDraft, id, comment, act, reviewAct]);

  if (!comment && !review) {
    return (
      <>
        <header className="chats-topbar chats-topbar--conv">
          <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack('/comments')}><BackIcon /></button>
          <h1 className="chats-topbar-title">Comments</h1>
        </header>
        <main className="chats-screen"><p className="chats-empty">{loaded ? 'This comment is no longer here.' : 'Opening…'}</p></main>
      </>
    );
  }

  const itemId = (comment ?? review)!.id;
  const platform = (comment?.platform ?? review?.platform)!;
  const where = PLATFORM[platform];
  const who = comment ? comment.author_name || `${where} user` : review!.reviewer_name || `${where} user`;
  const firstName = who.split(' ')[0];
  const open = comment ? waiting(comment) : reviewWaiting(review!);
  const leftAt = comment?.left_at ?? review?.left_at ?? null;
  const leftBy = comment?.left_by_name ?? review?.left_by_name ?? null;
  // Leo on duty for this comment, as the Chats switch: the country's setting unless someone flipped this one.
  const leoHere = !!comment && open && !comment.leo_off && (leoOn || !!comment.leo_send_at);
  const sendAt = open ? (comment?.leo_send_at ?? review?.leo_send_at ?? null) : null;
  const leoWillSend = comment ? leoHere && !!sendAt : !!sendAt;

  const run = async (op: string, extra: Record<string, unknown>, done: string) => {
    setBusy(true);
    setNotice(null);
    setPending(op);
    try {
      const out = comment ? await act(op, itemId, extra) : await reviewAct(op, itemId, extra);
      if (op === 'suggest' || op === 'tell_leo') {
        setMode('reply');
        setNotice(out.suggestion ? (op === 'tell_leo' ? "Told Leo ✓ His new suggestion is in his card" : "Leo's new suggestion is in his card ✓") : (out.reason ?? 'Leo has nothing to suggest'));
        return;
      }
      if (op === 'note') {
        setText('');
        setMode('reply');
        setNotice(out.not_reached?.length ? `Note added ✓ Not reached: ${out.not_reached.join(', ')} (they cannot open comments)` : 'Note added ✓');
        return;
      }
      // Every result is said in the conversation (the page agreed with Gary, 3 Nov 2026).
      setNotice(op === 'reply' ? `Sent to ${where} ✓` : `${done} ✓`);
      setChanging(false);
      // As Chats (Gary, 3 Nov 2026): you stay in the conversation and your reply shows as your message straight away.
      if (op === 'reply') setSent(String(extra.text ?? ''));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'That did not work. Try again.';
      // Already answered elsewhere (Meta): the reply that is there now shows in the conversation.
      if (/already replied/i.test(msg)) setNotice(`Already answered on ${where}, nothing more was sent. Our reply is shown above.`);
      else setNotice(`Not sent: ${msg}`);
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  /** Edit Leo's suggestion: his words go in the box and his timer stops, so he never sends the old version. */
  function editSuggestion(words: string) {
    setText(words);
    input.current?.focus({ preventScroll: true });
    if (sendAt) void run(comment ? 'comment_duty' : 'hold', comment ? { leo: false } : {}, '');
  }

  function markDone() {
    void run('leave', { left: true }, "Done. It's in the Done tab");
  }

  function startMode(m: 'note' | 'leo') {
    closeSheet();
    setMode(m);
    setText('');
    input.current?.focus({ preventScroll: true });
  }

  /** The + sheet, as in Chats: everything you can do with this comment or review. */
  function plusSheet() {
    openSheet(
      <>
        <SheetOption icon={<NoteIcon />} title="Team note" sub={`Only the team sees it, never ${firstName}`} onClick={() => startMode('note')} />
        {(!replied || changing) && <SheetOption icon={<SparkIcon />} title="Tell Leo" sub="Leo writes the reply again as you say" onClick={() => startMode('leo')} />}
        {(!replied || changing) && <SheetOption icon={<SparkIcon />} title="Leo again" sub="A fresh suggestion from Leo" onClick={() => { closeSheet(); void run('suggest', {}, ''); }} />}
        {replied && !changing && <SheetOption icon={<EditIcon />} title="Change reply" sub={`Edit our reply on ${where}`} onClick={() => { closeSheet(); setText(replied); setChanging(true); setMode('reply'); input.current?.focus({ preventScroll: true }); }} />}
        {leftAt && <SheetOption icon={<ReplyIcon />} title="Reopen" sub="It needs a reply after all" onClick={() => { closeSheet(); void run('leave', { left: false }, 'Back in Needs you'); }} />}
        {review?.reply_text && <SheetOption icon={<TrashIcon />} title="Delete reply" sub={`Takes our reply off ${where}`} danger onClick={() => openSheet(
          <>
            <p className="chats-sheet-hint">Take our reply off {where}? The review stays.</p>
            <SheetOption icon={<TrashIcon />} title="Yes, take it off" sub="This cannot be undone" danger onClick={() => { closeSheet(); void run('delete_reply', {}, 'Reply taken off'); }} />
          </>,
        )} />}
      </>,
    );
  }

  function flipDuty() {
    void run('comment_duty', { leo: !leoHere }, leoHere ? `You're on duty. Leo won't send this one` : 'Leo is on duty: he sends his reply in 10 minutes unless you act first');
  }

  function submit() {
    const value = text.trim();
    if (!value || busy) return;
    if (mode === 'note') void run('note', { text: value }, '');
    else if (mode === 'leo') { setText(''); void run('tell_leo', { text: value }, ''); }
    else void run('reply', { text: value }, `Reply sent to ${where}`);
  }

  // The conversation in time order: their words, our reply, the team's notes.
  const lines: Line[] = [
    { kind: 'theirs' as const, at: comment?.commented_at ?? review!.reviewed_at },
    ...(notes.get(itemId) ?? []).map((n) => ({ kind: 'note' as const, at: n.created_at, note: n })),
    ...(replied ? [{ kind: 'ours' as const, at: comment?.replied_at ?? review?.replied_at ?? new Date().toISOString() }] : []),
  ].sort((a, b) => a.at.localeCompare(b.at));
  const boxOpen = !replied || changing || mode !== 'reply';

  return (
    <>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn chats-back" aria-label="Back" onClick={() => goBack('/comments')}><BackIcon /></button>
        <span className="chats-conv-head">
          <Thumb url={comment?.post_picture_url ?? null} platform={platform} small />
          <span className="chats-conv-titles">
            <span className="chats-conv-name">{comment ? placeName(comment) : `${who} · ${stars(review!)}`}</span>
            <span className="chats-conv-sub">{where} · {pageName((comment ?? review)!.page_id)}</span>
          </span>
        </span>
      </header>
      {/* The top line as Chats (Gary, 23 Oct): who is on duty, one switch to flip. Reviews: a person always sends. */}
      <div className={`chats-timer chats-timer--${leoHere ? 'drc' : 'person'}`}>
        <span className="chats-timer-text">{comment ? `Public ${where} comment` : `${where} review`}</span>
        {comment && canAct && !comment.removed_at && (
          <button type="button" className="chats-timer-act" disabled={busy} onClick={() => void run('hide', { hidden: !comment.hidden }, comment.hidden ? `Shown again on ${where}` : `Hidden on ${where}`)}>
            {pending === 'hide' ? (comment.hidden ? 'Showing…' : 'Hiding…') : comment.hidden ? 'Show again' : 'Hide'}
          </button>
        )}
        {open && canAct && <button type="button" className="chats-timer-act" disabled={busy} onClick={markDone}>{pending === 'leave' ? 'Closing…' : 'Done'}</button>}
        {leftAt && canAct && <button type="button" className="chats-timer-act" disabled={busy} onClick={() => void run('leave', { left: false }, 'Back in Needs you')}>Reopen</button>}
        {comment && open && canAct && (
          <button type="button" className="chats-duty-flip" onClick={flipDuty} disabled={busy}
            aria-label={leoHere ? 'Leo is on duty. Switch to you' : 'You are on duty. Switch to Leo'}>
            <span className="chats-duty-flip-label">On duty</span>
            <span className={leoHere ? 'chats-duty-flip-off' : undefined}>You</span>
            <span className={`chats-switch${leoHere ? ' chats-switch--on' : ''}`} />
            <span className={leoHere ? undefined : 'chats-duty-flip-off'}>Leo</span>
          </button>
        )}
      </div>
      <main className="chats-thread chats-screen">
        {comment && (
          <div className="chats-cmt-where">
            <Thumb url={comment.post_picture_url} platform={platform} small />
            <span className="chats-cmt-where-text">
              <b>{comment.post_kind === 'ad' ? 'Our ad' : 'Our post'}{comment.post_permalink ? <> · <a href={comment.post_permalink} target="_blank" rel="noreferrer">Open on {where}</a></> : null}</b>
              {comment.post_text || 'No words on it.'}
            </span>
          </div>
        )}
        {lines.map((l) => {
          if (l.kind === 'theirs') return (
            <div key="theirs" className="chats-msg-wrap">
              <div className="chats-msg chats-msg--in">
                <div className="chats-msg-label">{who}</div>
                <span className="chats-msg-text">{comment ? comment.body || 'A sticker or photo' : review!.body || 'Rating only, no words.'}</span>
                <span className="chats-msg-meta">{when(l.at)}</span>
              </div>
            </div>
          );
          if (l.kind === 'ours') return (
            <div key="ours" className="chats-msg-wrap chats-msg-wrap--out">
              <div className={`chats-msg chats-msg--out${comment?.replied_by_leo ? ' chats-msg--leo' : ''}`}>
                <div className={`chats-msg-label${comment?.replied_by_leo ? '' : ' chats-msg-label--team'}`}>{comment?.replied_by_leo ? 'Leo' : (comment?.reply_by_name ?? review?.reply_by_name) || 'CleanMyBed'}</div>
                <span className="chats-msg-text">{replied}</span>
                <span className="chats-msg-meta">
                  {review?.reply_state === 'PENDING' ? 'Waiting for Google · ' : review?.reply_state === 'REJECTED' ? 'Refused by Google · ' : ''}
                  {(comment?.replied_at ?? review?.replied_at) ? when((comment?.replied_at ?? review?.replied_at)!) : ''}
                </span>
              </div>
            </div>
          );
          return (
            <div key={l.note.id} className="chats-msg-wrap chats-msg-wrap--out">
              <div className="chats-msg chats-msg--note">
                <div className="chats-msg-label">{l.note.kind === 'leo' ? `Told Leo${l.note.author_name ? `, ${l.note.author_name.split(' ')[0]}` : ''}` : `Team note from ${l.note.author_name ?? 'the team'}, ${firstName} can't see this`}</div>
                <span className="chats-msg-text">{l.note.body}</span>
                <span className="chats-msg-meta">{when(l.at)}</span>
              </div>
            </div>
          );
        })}
        {/* The Leo card (the page agreed with Gary, 3 Nov 2026): always there while it is open, the same three buttons
            every time. Send sends his words, Edit puts them in the box, Done closes it with no reply. */}
        {canAct && open && !replied && mode === 'reply' && (
          <>
            <div className="chats-msg-wrap chats-msg-wrap--out">
              <div className="chats-msg chats-msg--out chats-msg--leo">
                <div className="chats-msg-label"><AiLeo suffix={suggestion ? "'s suggestion, not sent" : ''} /></div>
                <span className="chats-msg-text">{
                  pending === 'suggest' || (needsDraft && !suggestion) ? 'Leo is writing a suggestion…'
                    : suggestion ? suggestion
                      : comment?.leo_kind === 'no_reply' ? `No reply needed. ${comment.leo_reason ?? ''}`.trim()
                        : (comment?.leo_reason ?? review?.leo_reason) || 'Leo has no suggestion for this one.'
                }</span>
                {comment?.leo_kind === 'person' && <span className="chats-msg-meta">This one needs you: {comment.leo_reason}</span>}
                {leoWillSend && sendAt && <span className="chats-msg-meta">Leo sends this in {Math.max(0, Math.ceil((Date.parse(sendAt) - Date.now()) / 60000))} min. Edit or Done stops him.</span>}
              </div>
            </div>
            <div className="chats-msg-wrap chats-msg-wrap--out">
              <div className="chats-drc-chips">
                <button type="button" disabled={busy || !suggestion} onClick={() => suggestion && void run('reply', { text: suggestion }, `Sent to ${where}`)}>{pending === 'reply' ? 'Sending…' : 'Send'}</button>
                <button type="button" disabled={busy || !suggestion} onClick={() => suggestion && editSuggestion(suggestion)}>Edit</button>
                <button type="button" disabled={busy} onClick={markDone}>{pending === 'leave' ? 'Closing…' : 'Done'}</button>
              </div>
            </div>
          </>
        )}
        {leftAt && <div className="chats-day">{leftBy === 'Leo' ? `Leo closed it: no reply needed${comment?.leo_reason ? `. ${comment.leo_reason}` : ''}` : `Done: no reply needed${leftBy ? ` (${leftBy})` : ''}`}</div>}
        {comment?.hidden && <div className="chats-day">Hidden from the public</div>}
        {!notice && (comment?.reply_error ?? review?.reply_error) && <div className="chats-day">Not sent: {comment?.reply_error ?? review?.reply_error}</div>}
        {pending && <div className="chats-day">{PENDING[pending]?.(where ?? '') ?? 'Working…'}</div>}
        {notice && <div className="chats-day">{notice}</div>}
      </main>

      {canAct && (
        <>
          {mode === 'note' && (
            <div className="chats-note-strip">
              <span>Team note. {firstName} won't see this</span>
              <button type="button" onClick={() => { setMode('reply'); setText(''); }}>Cancel</button>
            </div>
          )}
          {mode === 'leo' && (
            <div className="chats-note-strip">
              <span>Tell Leo. {firstName} won't see this</span>
              <button type="button" onClick={() => { setMode('reply'); setText(''); }}>Cancel</button>
            </div>
          )}
          {mode === 'reply' && replied && changing && <p className="chats-cmt-hint">Our reply as it shows on {where}. Change it and send to replace it.</p>}
          <form className="chats-composer" onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <button type="button" className="chats-plus" aria-label="Add: team note, Tell Leo, Leo again, change reply" onClick={plusSheet}><PlusIcon /></button>
            <div className={`chats-field${mode !== 'reply' ? ' chats-field--note' : ''}`}>
              <textarea ref={input} rows={1} value={text} onChange={(e) => setText(e.target.value)} disabled={!boxOpen}
                placeholder={mode === 'note' ? 'Note for the team (@name alerts them)' : mode === 'leo' ? 'What should Leo say or change?'
                  : !boxOpen ? 'Replied. + for a note or to change it' : needsDraft ? 'Leo is writing a suggestion…' : `Write a public reply on ${where}`}
                aria-label={mode === 'note' ? 'Team note' : mode === 'leo' ? 'Tell Leo' : 'Reply'}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }} />
            </div>
            <button type="submit" className={`chats-send${mode !== 'reply' ? ' chats-send--note' : ''}`} aria-label={mode === 'note' ? 'Add note' : mode === 'leo' ? 'Tell Leo' : 'Send'}
              title="Send (Ctrl or Cmd + Enter)" disabled={busy || !text.trim() || !boxOpen}
              onPointerDown={(e) => e.preventDefault()} onMouseDown={(e) => e.preventDefault()}><SendIcon /></button>
          </form>
        </>
      )}
    </>
  );
}
